import {GradeGrowthEvaluation, GrowthDecimal, GrowthPointsCondition, GrowthTraining,
  GrowthTrainingSource, GrowthLearningPreview, GrowthLearningScope, GrowthLearningLevel, GrowthForeignTraining} from './grade-growth.model';
import {DealPayrollGradeCode} from 'app/models/deal-payroll.model';
import {dealPayrollGradeKey} from 'app/models/deal-payroll-presentation';

const UNKNOWN_TEXT = '—';
const NUMBER_TYPE = 'number';
const STRING_TYPE = 'string';
const VALID = 'VALID';
const NOT_APPLICABLE = 'NOT_APPLICABLE';
const PREFIX = 'GRADE_GROWTH_EVALUATION_';
const UNKNOWN = 'UNKNOWN';
const STATES = ['READY', 'BLOCKED', 'UNKNOWN', 'NO_NEXT_GRADE'];
const FIRST = 0;
const DECIMAL = /^-?\d+(\.\d+)?([eE][+-]?\d+)?$/;
const SCIENTIFIC_DECIMAL = /^(-?)(\d+)(?:\.(\d+))?[eE]([+-]?\d+)$/;
const ZERO_DECIMAL_DIGITS = /^0+$/;
const DECIMAL_POINT = '.';
const ZERO_DIGIT = '0';
const EMPTY_TEXT = '';
const MAX_DECIMAL_EXPANSION = 10000;
const SATISFIED = 'GRADE_GROWTH_CONDITION_SATISFIED';
const REQUIRED = 'GRADE_GROWTH_CONDITION_REQUIRED';
const UNAVAILABLE = 'GRADE_GROWTH_CONDITION_UNKNOWN';
const INAPPLICABLE = 'GRADE_GROWTH_CONDITION_NOT_APPLICABLE';
const AUTHORITIES = ['LOCAL', 'PLANFIX', 'PF', 'INTERNAL'];
const TRAINING_STATES = ['SATISFIED', 'REQUIRED', 'BLOCKED', 'UNKNOWN', 'EXHAUSTED', 'CONFIGURATION_ERROR'];
const SOURCE_PREFIX = 'GRADE_GROWTH_AUTHORITY_';
const TRAINING_PREFIX = 'GRADE_GROWTH_TRAINING_';
const REASON_PREFIX = 'GRADE_GROWTH_REASON_';
const SCOPE_PREFIX = 'GRADE_GROWTH_SCOPE_';
const SUBDEPARTMENT_SCOPE_KEY = 'GRADE_GROWTH_SCOPE_SUBDEPARTMENT';
const PREVIEW_SPECIAL_STATES = ['ONBOARDING', 'UNSPECIFIED', UNKNOWN];
const PREVIEW_APPLIES = 'APPLIES';
const FOREIGN_SATISFIED = 'SATISFIED';
const FOREIGN_REQUIRED = 'REQUIRED';
const LEARNING_COMPLETE_STATES = ['SATISFIED', 'ONBOARDING', 'UNSPECIFIED'];
const LEARNING_REQUIRED_STATES = ['REQUIRED', 'BLOCKED', 'EXHAUSTED'];
const LEARNING_UNKNOWN_REASONS = ['GROWTH_TRAINING_CONTEXT_UNAVAILABLE', 'GROWTH_TRAINING_UNAVAILABLE',
  'GROWTH_TRAINING_UNKNOWN', 'GROWTH_TRAINING_CONFIGURATION_ERROR', 'GROWTH_TRAINING_LIMIT', 'GROWTH_LEARNING_OVERLAP_UNKNOWN'];
const LEARNING_REQUIRED_REASONS = ['MANDATORY_TRAINING_REQUIRED', 'COMPANY_TRAINING_REQUIRED',
  'GRADE_TRAINING_PERCENTAGE_REQUIRED', 'FOREIGN_TRAINING_REQUIRED', 'GROWTH_TRAINING_INCOMPLETE', 'GROWTH_TRAINING_REQUIRED'];
const APPLIED = 'APPLIED';
const READY = 'READY';
const DEPARTMENT_LEARNING_KEY = 'GRADE_GROWTH_REQUIRED_LEARNING_DEPARTMENT';
const SUBDEPARTMENT_LEARNING_KEY = 'GRADE_GROWTH_REQUIRED_LEARNING_SUBDEPARTMENT';
const ONE = 1;
const REASONS: {[code: string]: string} = {
  FOREIGN_TRAINING_REQUIRED: 'FOREIGN_TRAINING_REQUIRED',
  MANDATORY_TRAINING_REQUIRED: 'MANDATORY_TRAINING_REQUIRED',
  GRADE_TRAINING_PERCENTAGE_REQUIRED: 'TRAINING_PERCENTAGE_REQUIRED',
  GROWTH_TRAINING_CONTEXT_UNAVAILABLE: 'TRAINING_UNKNOWN',
  PROBATION_NOT_PASSED: 'PROBATION_NOT_PASSED', COMPANY_TRAINING_REQUIRED: 'COMPANY_TRAINING_REQUIRED',
  GROWTH_SAME_LEVEL_POINTS_INSUFFICIENT: 'POINTS', GROWTH_TOTAL_POINTS_INSUFFICIENT: 'POINTS',
  GROWTH_TRAINING_INCOMPLETE: 'TRAINING', GROWTH_TRAINING_UNAVAILABLE: 'CONFIGURATION',
  GROWTH_TRAINING_REQUIRED: 'TRAINING', GROWTH_TRAINING_UNKNOWN: 'TRAINING_UNKNOWN',
  GROWTH_TRAINING_CONFIGURATION_ERROR: 'CONFIGURATION', GROWTH_POLICY_UNKNOWN: 'CONFIGURATION',
  GROWTH_GRADE_MISSING: 'PAYROLL_GRADE_MISSING', GROWTH_INVALID_GRADE_INTERVAL: 'CONFIGURATION',
  GROWTH_TOTAL_POINTS_UNKNOWN: 'SOURCE', GROWTH_SAME_LEVEL_POINTS_UNKNOWN: 'SOURCE',
  GROWTH_SOURCE_UNKNOWN: 'SOURCE', GROWTH_SOURCE_CONFLICT: 'SOURCE', GROWTH_SOURCE_STALE: 'SOURCE',
  GROWTH_STORAGE_NOT_READY: 'STORAGE', GROWTH_FUTURE_ASSIGNMENT_PENDING: 'FUTURE',
  GROWTH_PROMOTION_COMMAND_REQUIRED: 'COMMAND', GROWTH_EVALUATION_CHANGED: 'REVIEW',
  GROWTH_INTERNAL_GRADE_INVALID: 'CONFIGURATION', GROWTH_CONTEXT_CHANGED: 'REVIEW',
  GROWTH_SOURCE_UNAVAILABLE: 'CONFIRMED_DATA_REQUIRED', GROWTH_LEGACY_OPERATION_REQUIRES_REVIEW: 'REVIEW',
  GROWTH_TRAINING_LIMIT: 'CONFIGURATION', GROWTH_HISTORY_REQUIRED: 'STORAGE',
  GROWTH_PAYROLL_GRADE_MISSING: 'PAYROLL_GRADE_MISSING',
  GROWTH_PAYROLL_GRADE_CONFLICT: 'PAYROLL_GRADE_CONFLICT',
  GROWTH_CONFIRMATION_CHANGED: 'CONFIRMATION_CHANGED', GROWTH_LEARNING_OVERLAP_UNKNOWN: 'CONFIRMED_DATA_REQUIRED',
  GROWTH_COMMANDS_DISABLED: 'COMMANDS_DISABLED', POLICY_GRADE_MISMATCH: 'ASSIGNMENT_MISMATCH'
};

export function growthAuthorityKey(source: string): string {
  return SOURCE_PREFIX + (AUTHORITIES.indexOf(source) >= FIRST ? source : UNKNOWN);
}

export function growthTrainingKey(status: string): string {
  return TRAINING_PREFIX + (TRAINING_STATES.indexOf(status) >= FIRST ? status : UNKNOWN);
}

export function growthSourceQualityKey(status: string): string {
  return status === VALID ? SATISFIED : UNAVAILABLE;
}

export function growthReasonKey(code: string): string {
  return REASON_PREFIX + (REASONS[code] || UNKNOWN);
}

export function growthDecimalText(value: GrowthDecimal): string {
  if (!isGrowthDecimal(value)) { return UNKNOWN_TEXT; }
  const text = String(value);
  const scientific = SCIENTIFIC_DECIMAL.exec(text);
  if (!scientific) { return text; }
  const [, sign, integer, fraction = EMPTY_TEXT, exponent] = scientific;
  const digits = integer + fraction;
  if (ZERO_DECIMAL_DIGITS.test(digits)) { return ZERO_DIGIT; }
  const point = integer.length + Number(exponent);
  if (!isFinite(point) || Math.abs(point) > MAX_DECIMAL_EXPANSION) { return UNKNOWN_TEXT; }
  if (point <= FIRST) { return sign + ZERO_DIGIT + DECIMAL_POINT + ZERO_DIGIT.repeat(-point) + digits; }
  if (point >= digits.length) { return sign + digits + ZERO_DIGIT.repeat(point - digits.length); }
  return sign + digits.slice(FIRST, point) + DECIMAL_POINT + digits.slice(point);
}

export function isGrowthDecimal(value: GrowthDecimal): boolean {
  if (typeof value === NUMBER_TYPE) { return isFinite(<number>value); }
  return typeof value === STRING_TYPE && DECIMAL.test(<string>value);
}

export function growthConditionKey(condition: GrowthPointsCondition): string {
  if (condition && condition.applicability === NOT_APPLICABLE) { return INAPPLICABLE; }
  if (!condition || condition.status !== VALID || condition.value == null || condition.satisfied == null) {
    return UNAVAILABLE;
  }
  return condition.satisfied ? SATISFIED : REQUIRED;
}

export function growthEvaluationKey(evaluation: GradeGrowthEvaluation): string {
  const state = evaluation && evaluation.state;
  return PREFIX + (STATES.indexOf(state) >= FIRST ? state : UNKNOWN);
}

export interface GrowthConditionView extends GrowthPointsCondition {
  valueText: string; requiredText: string; statusKey: string;
}
export interface GrowthTrainingView extends GrowthTraining {
  currentText: string; requiredText: string; statusKey: string;
  sources: Array<GrowthTrainingSource & {labelKey: string}>;
}
export interface GrowthEvaluationView {
  learningPreview: GrowthLearningPreviewView;
  stateKey: string; gradeKey: string; sourceKey: string; sourceStatusKey: string;
  payrollGradeKey: string; targetKey: string;
  observedGradeKey: string; observedSourceKey: string; observedStatusKey: string;
  total: GrowthConditionView; exact: GrowthConditionView;
  trainings: GrowthTrainingView[]; reasons: Array<{code: string; labelKey: string}>;
}

export function growthEvaluationView(evaluation: GradeGrowthEvaluation, preview?: GrowthLearningPreview): GrowthEvaluationView {
  if (!evaluation) { return null; }
  const grade = evaluation.growthGrade;
  const target: DealPayrollGradeCode = evaluation.target ? evaluation.target.code || null : null;
  return {learningPreview: growthLearningPreviewView(preview, target, evaluation),
    stateKey: growthEvaluationKey(evaluation), gradeKey: dealPayrollGradeKey(grade ? grade.code : null),
    sourceKey: growthAuthorityKey(grade && grade.source), sourceStatusKey: growthSourceQualityKey(grade && grade.status),
    payrollGradeKey: dealPayrollGradeKey(evaluation.payrollGrade ? evaluation.payrollGrade.code : null),
    observedGradeKey: dealPayrollGradeKey(evaluation.sourceGrade ? evaluation.sourceGrade.code : null),
    observedSourceKey: growthAuthorityKey(evaluation.sourceGrade && evaluation.sourceGrade.source),
    observedStatusKey: growthSourceQualityKey(evaluation.sourceGrade && evaluation.sourceGrade.status),
    targetKey: dealPayrollGradeKey(evaluation.target ? evaluation.target.code : null),
    total: conditionView(evaluation.totalPoints), exact: conditionView(evaluation.sameLevelTasks),
    trainings: (evaluation.trainings || []).map(trainingView),
    reasons: (evaluation.reasons || []).map(reason => ({code: reason.code, labelKey: growthReasonKey(reason.code)}))};
}

export interface GrowthLearningPreviewView {
  statusKey: string;
  lines: GrowthLearningCountView[];
  foreignTraining: GrowthForeignTrainingView;
}
export interface GrowthForeignTrainingView {
  ownCourses: number | null;
  completed: number | null;
  required: number | null;
  statusKey: string;
}
export interface GrowthLearningCountView {
  id: string; scopeKey: string; level: number | null;
  completed: number | null; required: number | null;
}

export function trackGrowthLearningCount(_index: number, line: GrowthLearningCountView): string { return line.id; }

export function growthLearningPreviewView(preview: GrowthLearningPreview, grade: DealPayrollGradeCode,
  evaluation?: GradeGrowthEvaluation): GrowthLearningPreviewView {
  const foreignTraining = foreignTrainingView(preview && preview.known !== false ? preview.foreignTraining : null);
  const row = preview && (preview.grades || []).filter(item => item.gradeCode === grade)[FIRST];
  const statusKey = learningConditionKey(evaluation, row && row.status, foreignTraining.statusKey);
  if (!row || PREVIEW_SPECIAL_STATES.indexOf(row.status) >= FIRST) { return {lines: [], foreignTraining: foreignTraining, statusKey: statusKey}; }
  const lines = learningCounts(row.department, DEPARTMENT_LEARNING_KEY);
  return {statusKey: statusKey, foreignTraining: foreignTraining, lines: row.subdepartment && row.subdepartment.applicability === NOT_APPLICABLE ? lines
    : lines.concat(learningCounts(row.subdepartment, SUBDEPARTMENT_LEARNING_KEY))};
}

function learningConditionKey(evaluation: GradeGrowthEvaluation, ownStatus: string, foreignStatus: string): string {
  if (!evaluation || !Array.isArray(evaluation.reasons) || !Array.isArray(evaluation.trainings)) { return UNAVAILABLE; }
  const reasons = evaluation.reasons.map(reason => reason.code);
  const statuses = [ownStatus].concat(evaluation.trainings.map(training => training.status));
  if (foreignStatus === UNAVAILABLE || statuses.some(status => LEARNING_COMPLETE_STATES.indexOf(status) < FIRST
    && LEARNING_REQUIRED_STATES.indexOf(status) < FIRST)
    || reasons.some(code => LEARNING_UNKNOWN_REASONS.indexOf(code) >= FIRST)) { return UNAVAILABLE; }
  if (foreignStatus === REQUIRED || statuses.some(status => LEARNING_REQUIRED_STATES.indexOf(status) >= FIRST)
    || reasons.some(code => LEARNING_REQUIRED_REASONS.indexOf(code) >= FIRST)) { return REQUIRED; }
  return evaluation.applicationStatus === APPLIED || evaluation.state === READY ? SATISFIED : UNAVAILABLE;
}

function foreignTrainingView(training: GrowthForeignTraining): GrowthForeignTrainingView {
  const known = training && (training.status === FOREIGN_SATISFIED || training.status === FOREIGN_REQUIRED)
    && [training.ownCourses, training.completedCourses, training.requiredCourses].every(knownCourseCount);
  return {ownCourses: known ? training.ownCourses : null,
    completed: known ? training.completedCourses : null, required: known ? training.requiredCourses : null,
    statusKey: !known ? UNAVAILABLE : training.status === FOREIGN_SATISFIED ? SATISFIED : REQUIRED};
}

function knownCourseCount(count: number): boolean {
  return typeof count === NUMBER_TYPE && isFinite(count) && count >= FIRST && Math.floor(count) === count;
}

function learningCounts(scope: GrowthLearningScope, key: string): GrowthLearningCountView[] {
  if (scope && scope.applicability === NOT_APPLICABLE) {
    return [{id: key, scopeKey: key, level: null, completed: FIRST, required: FIRST}];
  }
  if (!scope || scope.applicability !== PREVIEW_APPLIES || !(scope.levels || []).length) {
    return [{id: key, scopeKey: key, level: null, completed: null, required: null}];
  }
  const empty = scope.levels.every(level => knownLearningCount(level) && level.completedCourses === FIRST && level.requiredCourses === FIRST);
  return (empty ? scope.levels.slice(FIRST, ONE) : scope.levels).map(level => ({id: key + level.level, scopeKey: key,
    level: !empty ? level.level : null,
    completed: knownLearningCount(level) ? level.completedCourses : null,
    required: knownLearningCount(level) ? level.requiredCourses : null}));
}

function knownLearningCount(level: GrowthLearningLevel): boolean {
  return level.status !== UNKNOWN && typeof level.completedCourses === NUMBER_TYPE && typeof level.requiredCourses === NUMBER_TYPE
    && isFinite(level.completedCourses) && isFinite(level.requiredCourses) && level.completedCourses >= FIRST && level.requiredCourses >= FIRST;
}

function conditionView(condition: GrowthPointsCondition): GrowthConditionView {
  if (!condition) { return null; }
  return Object.assign({}, condition, {valueText: growthDecimalText(condition.value),
    requiredText: growthDecimalText(condition.required), statusKey: growthConditionKey(condition)});
}

function trainingView(training: GrowthTraining): GrowthTrainingView {
  return Object.assign({}, training, {currentText: growthDecimalText(training.currentLevel),
    requiredText: growthDecimalText(training.requiredLevel), statusKey: growthTrainingKey(training.status),
    sources: (training.sources || []).map(source => Object.assign({}, source, {
      labelKey: source.subdepartment ? SUBDEPARTMENT_SCOPE_KEY : SCOPE_PREFIX + source.scope}))});
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/grade-growth/grade-growth-presentation.ts